import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Card,
  CardContent
} from '@mui/material';
import {
  Add,
  Delete,
  Email,
  PersonAdd,
  Description,
  CheckCircle
} from '@mui/icons-material';
import api from '../lib/api';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

interface SendForSignatureProps {
  agreementId: string;
  agreement: any;
}

const SendForSignature: React.FC<SendForSignatureProps> = ({ agreementId, agreement }) => {
  const queryClient = useQueryClient();
  const [openDialog, setOpenDialog] = useState(false);
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState(`Please sign: ${agreement?.agreementNumber}`);
  const [message, setMessage] = useState(`You've been invited to sign the Trust Agreement ${agreement?.agreementNumber}. Please review and sign.`);

  const { data: signatures } = useQuery({
    queryKey: ['signatures', agreementId],
    queryFn: async () => {
      const res = await api.get(`/agreements/${agreementId}/signatures`);
      return res.data.data;
    },
    enabled: !!agreementId
  });

  const sendMutation = useMutation({
    mutationFn: async (signerEmail: string) => {
      const res = await api.post(`/agreements/${agreementId}/send-signature-request`, {
        signerEmail,
        subject,
        message
      });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['signatures', agreementId] });
      setOpenDialog(false);
      setEmail('');
    }
  });

  const handleOpenDialog = () => setOpenDialog(true);
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEmail('');
  };

  const handleSend = () => {
    if (!email) return;
    sendMutation.mutate(email);
  };

  const signedEmails = signatures?.map((sig: any) => sig.signer.email) || [];
  const pendingSigners = agreement?.clients?.filter((ac: any) => !signedEmails.includes(ac.client.email)) || [];

  return (
    <Box>
      {/* Summary */}
      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', p: 3, mb: 3 }}>
        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <Description color="primary" sx={{ fontSize: 40 }} />
          <Box>
            <Typography variant="h6">Signature Request</Typography>
            <Typography variant="body2" color="text.secondary">
              Manage who needs to sign this agreement
            </Typography>
          </Box>
        </Box>
        <Box display="flex" gap={2} flexWrap="wrap">
          <Chip label={`${signedEmails.length} Signed`} color="success" />
          <Chip label={`${pendingSigners.length} Pending`} color="warning" />
          <Chip label={`${agreement?.clients?.length || 0} Total`} />
        </Box>
      </Paper>

      {/* Pending Signers */}
      {pendingSigners.length > 0 && (
        <Box mb={3}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="subtitle1" fontWeight="bold">
              Pending Signatures
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<PersonAdd />}
              onClick={handleOpenDialog}
            >
              Add Signer
            </Button>
          </Box>
          <List>
            {pendingSigners.map((agreementClient: any) => (
              <Card key={agreementClient.id} variant="outlined" sx={{ mb: 1 }}>
                <CardContent sx={{ py: 2 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box display="flex" alignItems="center">
                      <Avatar sx={{ width: 32, height: 32, mr: 2 }}>
                        {agreementClient.client.firstName[0]}
                      </Avatar>
                      <Box>
                        <Typography variant="body1">
                          {agreementClient.client.firstName} {agreementClient.client.lastName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {agreementClient.client.email} • {agreementClient.clientType}
                        </Typography>
                      </Box>
                    </Box>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<Email />}
                    >
                      Send Request
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </List>
        </Box>
      )}

      {/* Signed */}
      {signatures && signatures.length > 0 && (
        <Box>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Signed
          </Typography>
          <List>
            {signatures.map((sig: any) => (
              <Card key={sig.id} variant="outlined" sx={{ mb: 1, bgcolor: 'success.light', opacity: 0.9 }}>
                <CardContent sx={{ py: 2 }}>
                  <Box display="flex" alignItems="center">
                    <CheckCircle color="success" sx={{ mr: 2 }} />
                    <Box>
                      <Typography variant="body1">
                        {sig.signer.firstName} {sig.signer.lastName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {sig.signer.email} • {new Date(sig.signedAt).toLocaleString()}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </List>
        </Box>
      )}

      {/* Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Send Signature Request</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <TextField
              fullWidth
              label="Subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
            <TextField
              fullWidth
              label="Message"
              multiline
              rows={4}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSend}
            disabled={!email || sendMutation.isPending}
          >
            Send Request
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SendForSignature;

